import { z } from 'zod';
import { ValidationEntrySeveritySchema } from '../../../../../react-commons/components/ValidationEntrySeverity';
import { ResourceTypeSchema } from '../../enumerations/ResourceType';
import { ValidationTypeSchema } from '../../enumerations/ValidationType';
import { VersionedArtifactResourceSchema } from '../../resource-base/artifactResourceTypes';
import { IdSchema, NameSchema, UseOrRemoveSchema } from '../../resource-base/baseSchemas';
import { msObject } from '../../resource-base/msObject';
import { PersistableInfoSchema } from '../../resource-base/persistableResourceTypes';
import { ValidationResourceSchema } from './detailedValidationResource';

// TODO Artifact/Persistable Extracts shall be separated
const ExtractResourceSchema = msObject({
    id: IdSchema,
    objectId: IdSchema.nullable(),
    name: NameSchema.optional(),
    // TODO schema
    persistableType: UseOrRemoveSchema,
});

const ExtractResourceWithPersistableTypeSchema = ExtractResourceSchema.extend({
    persistableType: z.string(),
});

export const UnsupportedArtifactsSchema = msObject({
    artifactType: z.string(),
    artifacts: ExtractResourceWithPersistableTypeSchema.array(),
});

export const DependencyValidationResultSchema = msObject({
    extract: ExtractResourceWithPersistableTypeSchema,
    validationResultMap: ValidationResourceSchema,
});

const SideEffectValidationResultSchema = msObject({
    extract: ExtractResourceSchema.nullable(),
    persistableType: z.string(),
    get validationResult() {
        return MutationValidationResourceSchema.nullable();
    },
});

const SideEffectDeletionValidationResultSchema = msObject({
    persistableType: z.string(),
    get validationResult() {
        return MutationValidationResourceSchema.nullable();
    },
});

const SideEffectValidationResultMapSchema = msObject({
    maxSeverity: ValidationEntrySeveritySchema.nullish(),
    updateResourceValidationResults: z.record(z.string(), SideEffectValidationResultSchema.array()).nullable(),
    addResourceValidationResults: z.record(z.string(), SideEffectValidationResultSchema.array()).nullable(),
    deleteResourceValidationResults: z.record(z.string(), SideEffectDeletionValidationResultSchema.array()).nullable(),
});

export const MutationValidationResourceSchema = msObject({
    validationType: ValidationTypeSchema,
    successful: z.boolean(),
    changed: z.boolean(),
    maxSeverity: ValidationEntrySeveritySchema.nullable(),
    fatalThreshold: ValidationEntrySeveritySchema.nullable(),
    content: ExtractResourceSchema.nullable(),
    changedProperties: z.string().array().nullable(),
    nonUpdatablePropertiesChanged: z.string().array().nullable(),
    validationResult: ValidationResourceSchema.nullable(),
    sideEffectResult: SideEffectValidationResultMapSchema.nullable(),
    dependencyValidationResult: z
        .record(z.string(), z.record(z.string(), z.record(z.string(), DependencyValidationResultSchema)))
        .nullable(),
    referencedVersionsNotClosed: z
        .record(
            z.string(),
            VersionedArtifactResourceSchema.extend({
                persistableInfo: PersistableInfoSchema.optional(),
                resourceType: ResourceTypeSchema.optional(),
                systemAnnotations: z.record(z.string(), z.string()).nullable().optional(),
                userAnnotations: z.record(z.string(), z.string()).nullable().optional(),
                persistableType: UseOrRemoveSchema,
            }).array()
        )
        .nullable(),
});

export type ExtractResourceWithPersistableType = z.infer<typeof ExtractResourceWithPersistableTypeSchema>;
export type ExtractResource = z.infer<typeof ExtractResourceSchema>;
export type UnsupportedArtifacts = z.infer<typeof UnsupportedArtifactsSchema>;
export type DependencyValidationResult = z.infer<typeof DependencyValidationResultSchema>;
export type SideEffectValidationResult = z.infer<typeof SideEffectValidationResultSchema>;
export type SideEffectDeletionValidationResult = z.infer<typeof SideEffectDeletionValidationResultSchema>;
export type SideEffectValidationResultMap = z.infer<typeof SideEffectValidationResultMapSchema>;
export type MutationValidationResource = z.infer<typeof MutationValidationResourceSchema>;
